import * as React from 'react';

import {IconAvatar} from 'shells/avatar/icon-avatar';
import {colors} from 'shells/colors/colors';
import {FilestackButton} from 'shells/email-editor/filestack-button';
import {CloudUploadIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {ChatIconEnum} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {ChatEngagementProUpsellModal} from '../../../../limits/feature-gates/chat-engagement-pro-upsell-modal';

import styles from './picker.module.css';

type Props = {
    type: ChatIconEnum;
    url: string | null | undefined;
    handleUpdate: (iconType: ChatIconEnum, iconUrl: string) => void;
};

export const DEFAULT_ICONS: Partial<Record<ChatIconEnum, string>> = {
    WAVE: '\u{1F44B}', // 👋
    CHAT_BUBBLE: '\u{1F4AC}', // 💬
    SMILEY_FACE: '\u{1F600}', // 😀
};

export function WidgetIconPicker(props: Props) {
    const {type, url} = props;
    const [customIconUrl, setCustomIconUrl] = React.useState<string | null | undefined>(null);
    const [isCustomIconUpsellModalOpen, setIsCustomIconUpsellModalOpen] = React.useState(false);

    const {user} = useGetSessionUser();
    const hasEngagementPro = user && user.permissions.canUseSms;

    // Keep track of custom icon URL when one is set
    React.useEffect(() => {
        if (type === 'CUSTOM' && url) {
            setCustomIconUrl(url);
        }
    }, [type, url]);

    const handleUploadClick = React.useCallback(() => {
        if (!hasEngagementPro) {
            setIsCustomIconUpsellModalOpen(true);
        }
    }, [hasEngagementPro]);

    return (
        <>
            <StackLegacy isHorizontal={true} spacing={8}>
                {Object.keys(DEFAULT_ICONS).map((icon) => (
                    <button
                        key={icon}
                        // @ts-expect-error
                        onClick={() => props.handleUpdate(icon, '')}
                        className={styles['icon-picker-button']}
                    >
                        <IconAvatar
                            key={icon}
                            // @ts-expect-error
                            customIcon={DEFAULT_ICONS[icon]}
                            avatarSize={48}
                            hasBorder={true}
                            iconSize={24}
                            backgroundColor={type === icon ? colors.blueBg : colors.white}
                            borderColor={type === icon ? colors.blue : colors.offWhiteDk}
                        />
                    </button>
                ))}
                {customIconUrl && (
                    <button
                        onClick={() => {
                            props.handleUpdate('CUSTOM', customIconUrl);
                        }}
                    >
                        <img
                            src={customIconUrl}
                            className={
                                type === 'CUSTOM'
                                    ? styles['custom-image-selected']
                                    : styles['custom-image']
                            }
                        />
                    </button>
                )}
                <div className={styles['icon-picker-button']}>
                    {hasEngagementPro ? (
                        <FilestackButton
                            isMultiSelect={false}
                            options={{maxSize: 5242880, accept: 'image/*'}}
                            buttonContent={
                                <IconAvatar
                                    icon={CloudUploadIcon}
                                    avatarSize={48}
                                    hasBorder={true}
                                    iconSize={24}
                                    backgroundColor={colors.white}
                                />
                            }
                            handleFilesUploaded={({filesUploaded}) => {
                                filesUploaded.forEach((file) => {
                                    setCustomIconUrl(file.url);
                                    props.handleUpdate('CUSTOM', file.url);
                                });
                            }}
                        />
                    ) : (
                        <button onClick={handleUploadClick}>
                            <IconAvatar
                                icon={CloudUploadIcon}
                                avatarSize={48}
                                hasBorder={true}
                                iconSize={24}
                                backgroundColor={colors.white}
                            />
                        </button>
                    )}
                </div>
            </StackLegacy>
            <ChatEngagementProUpsellModal
                isOpen={isCustomIconUpsellModalOpen}
                onClose={() => setIsCustomIconUpsellModalOpen(false)}
            />
        </>
    );
}
